import { DriverDay_Read, ShiftAdjustmentType } from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

import { CompanyBasic } from '~hooks/useCompany/models';
import { DriverBasic } from '~hooks/useDrivers/models';
import { DeliveredQuantity } from '~hooks/useOrders/models';
import { Nullable } from '~types/Nullable';

export class DriverDay {
  public static parse({
    id,
    started_shift_at,
    ended_shift_at,
    adjusted_started_shift_at,
    adjusted_ended_shift_at,
    jobs_count,
    loads_count,
    tickets_count,
    first_geofence_entry_at,
    last_geofence_exit_at,
    first_ticket_at,
    last_ticket_at,
    total_break_minutes,
    total_gps_minutes,
    delivered_quantities,
    driver,
    company,
    vendor_account,
    equipment_external_ids,
    driver_day_date,
    created_at,
    updated_at,
    total_shift_minutes,
    shift_adjustment_minutes,
    shift_adjustment_type,
    approvable,
    approved,
    unapprovable,
    external_id,
    editable,
    driver_day_id,
    billing_approved_at,
    field_approved_at,
    travelled_distance_meters,
    idle_time_seconds,
    notes,
    discarded_at,
  }: DriverDay_Read): DriverDay {
    return new DriverDay(
      id,
      started_shift_at ? dayjs(started_shift_at) : null,
      ended_shift_at ? dayjs(ended_shift_at) : null,
      adjusted_started_shift_at ? dayjs(adjusted_started_shift_at) : null,
      adjusted_ended_shift_at ? dayjs(adjusted_ended_shift_at) : null,
      jobs_count,
      loads_count,
      tickets_count,
      first_geofence_entry_at ? dayjs(first_geofence_entry_at) : null,
      last_geofence_exit_at ? dayjs(last_geofence_exit_at) : null,
      first_ticket_at ? dayjs(first_ticket_at) : null,
      last_ticket_at ? dayjs(last_ticket_at) : null,
      total_break_minutes ?? null,
      total_gps_minutes,
      delivered_quantities.map(DeliveredQuantity.parse),
      DriverBasic.parse(driver),
      CompanyBasic.parse(company),
      vendor_account?.name ?? null,
      equipment_external_ids,
      dayjs(driver_day_date),
      dayjs(created_at),
      dayjs(updated_at),
      total_shift_minutes ?? null,
      shift_adjustment_minutes,
      shift_adjustment_type,
      approvable,
      approved,
      unapprovable,
      external_id,
      editable,
      driver_day_id,
      billing_approved_at ? dayjs(billing_approved_at) : null,
      field_approved_at ? dayjs(field_approved_at) : null,
      travelled_distance_meters ?? null,
      idle_time_seconds,
      notes ?? null,
      discarded_at ? dayjs(discarded_at) : null,
    );
  }

  public get id() {
    return this._id;
  }

  public get startedShiftAt() {
    return this._started_shift_at;
  }

  public get endedShiftAt() {
    return this._ended_shift_at;
  }

  public get adjustedStartedShiftAt() {
    return this._adjusted_started_shift_at;
  }

  public get adjustedEndedShiftAt() {
    return this._adjusted_ended_shift_at;
  }

  public get jobCount() {
    return this._jobs_count;
  }

  public get loadsCount() {
    return this._loads_count;
  }

  public get ticketsCount() {
    return this._tickets_count;
  }

  public get firstGeofenceEntryAt() {
    return this._first_geofence_entry_at;
  }

  public get lastGeofenceExitAt() {
    return this._last_geofence_exit_at;
  }

  public get firstTicketAt() {
    return this._first_ticket_at;
  }

  public get lastTicketAt() {
    return this._last_ticket_at;
  }

  public get totalBreakMinutes() {
    return this._total_break_minutes;
  }

  public get totalGpsMinutes() {
    return this._total_gps_minutes;
  }

  public get deliveredQuantities() {
    return this._delivered_quantities;
  }

  public get driver() {
    return this._driver;
  }

  public get company() {
    return this._company;
  }

  public get vendorName() {
    return this._vendor_name;
  }

  public get equipmentExternalIds() {
    return this._equipment_external_ids;
  }

  public get driverDayDate() {
    return this._driver_day_date;
  }

  public get createdAt() {
    return this._created_at;
  }

  public get updatedAt() {
    return this._updated_at;
  }

  public get totalShiftMinutes() {
    return this._total_shift_minutes;
  }

  public get shiftAdjustmentMinutes() {
    return this._shift_adjustment_minutes;
  }

  public get shiftAdjustmentType() {
    return this._shift_adjustment_type;
  }

  public get approvable() {
    return this._approvable;
  }

  public set approvable(approvable: boolean) {
    this._approvable = approvable;
  }

  public get approved() {
    return this._approved;
  }

  public get unapprovable() {
    return this._unapprovable;
  }

  public set unapprovable(unapprovable: boolean) {
    this._unapprovable = unapprovable;
  }

  public get externalId() {
    return this._external_id;
  }

  public get editable() {
    return this._editable;
  }

  public get driverDayId() {
    return this._driver_day_id;
  }

  public get billingApprovedAt() {
    return this._billing_approved_at;
  }

  public get fieldApprovedAt() {
    return this._field_approved_at;
  }

  constructor(
    private _id: string,
    private _started_shift_at: Dayjs | null,
    private _ended_shift_at: Dayjs | null,
    private _adjusted_started_shift_at: Dayjs | null,
    private _adjusted_ended_shift_at: Dayjs | null,
    private _jobs_count: number,
    private _loads_count: number,
    private _tickets_count: number,
    private _first_geofence_entry_at: Dayjs | null,
    private _last_geofence_exit_at: Dayjs | null,
    private _first_ticket_at: Dayjs | null,
    private _last_ticket_at: Dayjs | null,
    private _total_break_minutes: number | null,
    private _total_gps_minutes: number,
    private _delivered_quantities: DeliveredQuantity[],
    private _driver: DriverBasic,
    private _company: CompanyBasic,
    private _vendor_name: string | null,
    private _equipment_external_ids: string[],
    private _driver_day_date: Dayjs,
    private _created_at: Dayjs,
    private _updated_at: Dayjs,
    private _total_shift_minutes: number | null,
    private _shift_adjustment_minutes: number,
    private _shift_adjustment_type: ShiftAdjustmentType | null,
    private _approvable: boolean,
    private _approved: boolean,
    private _unapprovable: boolean,
    private _external_id: string | null,
    private _editable: boolean,
    private _driver_day_id: string,
    private _billing_approved_at: Nullable<Dayjs>,
    private _field_approved_at: Nullable<Dayjs>,
    private _travelled_distance_meters: string | null,
    private _idle_time_seconds: number,
    private _notes: string | null,
    private _discarded_at: Dayjs | null = null,
  ) {}

  public get travelledDistanceMeters() {
    return this._travelled_distance_meters;
  }

  public get idleTimeSeconds() {
    return this._idle_time_seconds;
  }

  public get notes() {
    return this._notes;
  }

  public get discardedAt() {
    return this._discarded_at;
  }
}
